import React from 'react';
import { Card, Badge, phone } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { genderKey, maritalStatusKey, allergySeverityKey } from '../../lib/patients.js';
import { MobileInfoRow, MobileSectionTitle } from './MobileKit.jsx';

// خلاصه — who this patient is, and what they are allergic to.
//
// The desktop lays the twelve demographic fields out on a
// `repeat(auto-fill, minmax(180px, 1fr))` grid. On a phone that collapses to one
// column anyway, but with the label ABOVE the value — two lines per fact and
// twenty-four lines of scroll. One full-width row per fact, label at the
// inline-start and value at the inline-end, halves it and keeps a long value
// from ever forcing a horizontal scroll (§4B, §6).
//
// The allergy banner is NOT repeated here: on this surface it is the sticky
// strip above the section navigator (§4C ③), which is strictly better than a
// banner that scrolls away.

const MobilePatientSummary = ({ patient }) => {
  const { t, formatNumber, formatDigits, formatDate } = useTranslation();
  const allergies = patient.allergies || [];
  const gKey = genderKey(patient.gender);
  const mKey = maritalStatusKey(patient.maritalStatus);

  return (
    <div data-testid="patient-summary">
      <MobileSectionTitle>{t('patient.summary.demographics')}</MobileSectionTitle>
      <Card style={{ padding: '0.25rem 0.875rem' }}>
        <MobileInfoRow
          label={t('common.labels.phone')}
          value={patient.phone ? formatDigits(phone.forDisplay(patient.phone)) : null}
          ltr
        />
        <MobileInfoRow
          label={t('common.labels.nationalId')}
          value={patient.nationalId ? formatDigits(patient.nationalId) : null}
          ltr
        />
        <MobileInfoRow
          label={t('common.labels.fileNumber')}
          value={patient.fileNumber ? formatDigits(patient.fileNumber) : null}
        />
        {/* birth_date is free text server-side with no validator, so it is
            RENDERED, never parsed into something prettier. `age` is the
            service's own derivation and is shown beside it. */}
        <MobileInfoRow
          label={t('patient.fields.birthDate')}
          value={patient.birthDate ? formatDigits(patient.birthDate) : null}
        />
        <MobileInfoRow
          label={t('patient.stats.age')}
          value={patient.age != null ? t('common.units.years', { count: patient.age }) : null}
        />
        <MobileInfoRow
          label={t('patient.fields.gender')}
          value={gKey ? t(gKey) : patient.gender}
        />
        <MobileInfoRow label={t('patient.fields.maritalStatus')} value={mKey ? t(mKey) : patient.maritalStatus} />
        <MobileInfoRow label={t('patient.fields.bloodType')} value={patient.bloodType} ltr />
        <MobileInfoRow label={t('patient.fields.job')} value={patient.job} />
        <MobileInfoRow label={t('patient.fields.insurance')} value={patient.insurance} />
        <MobileInfoRow
          label={t('patient.fields.height')}
          value={patient.height ? formatNumber(patient.height) : null}
        />
        <MobileInfoRow
          label={t('patient.fields.createdAt')}
          value={patient.createdAt ? formatDate(patient.createdAt, 'numeric') : null}
        />
      </Card>

      <MobileSectionTitle style={{ marginTop: '0.875rem' }}>
        {t('patient.summary.allergies')}
      </MobileSectionTitle>
      <Card style={{ padding: '0.875rem' }}>
        {allergies.length === 0 ? (
          <div style={{ fontSize: '0.8125rem', color: C.textMuted }}>
            {t('patient.summary.noAllergies')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {allergies.map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                  {a.allergen}
                </span>
                {/* A recognised severity is labelled; anything else renders
                    exactly as it was recorded. */}
                {a.severity && (
                  <Badge variant="outline">
                    {allergySeverityKey(a.severity) ? t(allergySeverityKey(a.severity)) : a.severity}
                  </Badge>
                )}
                {a.reaction && (
                  <span style={{ fontSize: '0.75rem', color: C.textSecondary }}>{a.reaction}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MobilePatientSummary;
